import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import {
  type ColumnDef,
  type ColumnFiltersState,
  type OnChangeFn,
  type SortingState,
  type Table as TableType,
  type RowData,
  type TableMeta,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Settings2, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Per-column presentation, read off `columnDef.meta`.
 *
 * `width` is a pixel width for a fixed-layout table. Reach for it when a
 * column's content varies in width between rows — a badge under a number in
 * one row and a bare number in the rest — because an auto-layout table sizes
 * each column to its widest cell, which lets a single row push that column
 * out of line with the same column in every other row.
 */
export interface DataTableColumnMeta {
  className?: string;
  /** Header-only classes, for a header that is sized differently to its cells. */
  headerClassName?: string;
  width?: number;
}

// Merges with the `className` augmentation declared alongside the locker-room
// table: both are module augmentations, so a column can be typed at the point
// it declares its meta rather than cast at every point one is read.
declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Pixel width for a fixed-layout table. See DataTableColumnMeta. */
    width?: number;
    headerClassName?: string;
  }
}

function columnMeta(columnDef: {
  meta?: unknown;
}): DataTableColumnMeta | undefined {
  return columnDef.meta as DataTableColumnMeta | undefined;
}

function widthStyle(columnDef: { meta?: unknown }) {
  const width = columnMeta(columnDef)?.width;
  return width == null ? undefined : { width };
}

function humanizeColumnId(id: string): string {
  return id
    .replace(/Utc$/i, "")
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

const PAGE_SIZE = 8;

function resolveClientPageSize(
  rowCount: number,
  paginateAfter: number | undefined,
  initialPageSize: number | undefined,
): number {
  if (paginateAfter != null && rowCount <= paginateAfter) {
    return Math.max(rowCount, 1);
  }
  return initialPageSize ?? PAGE_SIZE;
}

const SKELETON_ROWS = 8;
const SKELETON_CELL_WIDTHS = [
  "w-36",
  "w-20",
  "w-10",
  "w-14",
  "w-14",
  "w-20",
  "w-20",
  "w-24",
  "w-3",
  "w-5",
];

/** Clicks on nested controls should not bubble up as a row selection. */
function shouldIgnoreRowClick(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return true;
  }
  return !!target.closest(
    'a, button, input, select, textarea, [role="menuitem"], [role="checkbox"], [data-no-row-click]',
  );
}

export interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  filterBar?: (table: TableType<TData>) => ReactNode;
  action?: ReactNode;
  isLoading?: boolean;
  emptyState?: ReactNode;
  meta?: TableMeta<TData>;
  /**
   * Noun shown in the pagination summary ("1–7 of 12 leagues"). Defaults to
   * "result". Provide `plural` only for irregular plurals.
   */
  itemNoun?: { singular: string; plural?: string };
  /**
   * Columns hidden on first render (e.g. a filter-only column). Keyed by column
   * id → visible. Columns with `enableHiding: false` stay out of the toggle menu.
   */
  initialColumnVisibility?: VisibilityState;
  /**
   * Controlled column-filter state. Provide with `onColumnFiltersChange` to lift
   * filters out of the table (e.g. to persist them across navigation). Omit for
   * the default internal state.
   */
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
  /**
   * Controlled sort state. Provide with `onSortingChange` to lift sort out of
   * the table (e.g. dashboard session). Omit for the default internal state.
   */
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  /**
   * Opt into server-driven pagination: `data` is treated as the current page
   * (no client slicing/filtering) and paging is delegated to the caller. Omit
   * for the default client-side pagination.
   */
  serverPagination?: {
    /** 0-based index of the current page. */
    pageIndex: number;
    pageSize: number;
    totalRows: number;
    onPageChange: (pageIndex: number) => void;
  };
  /**
   * 'embedded' blends the table into a parent container: it drops the outer card
   * (border/background), the Columns toolbar, and the muted header fill, and
   * hides the pagination bar while everything fits on one page. Defaults to the
   * standalone 'default' card. Opt-in — existing callers are unaffected.
   */
  variant?: "default" | "embedded";
  /** Client page size. Defaults to 8. Ignored when `serverPagination` is set. */
  initialPageSize?: number;
  /**
   * When the row count is at or below this number, every row is shown and
   * paging is skipped. Pagination starts only above the threshold.
   */
  paginateAfter?: number;
  /**
   * Keep the footer count visible in embedded tables even when everything
   * fits on one page. Pager controls still hide until there is a second page.
   */
  alwaysShowFooter?: boolean;
  /**
   * Initial column sort. Used only on first mount — later header clicks stay
   * in local table state. Omit for unsorted.
   */
  initialSorting?: SortingState;
  /**
   * When set, the full row is clickable (with hover/active feedback). Clicks on
   * nested links, buttons, and menus are ignored so cell actions keep working.
   */
  onRowClick?: (row: TData) => void;
  /**
   * Extra classes for one row, by its data. Use it to mark a row's state (the
   * league page tints the round being played); the classes land on the `tr`, so
   * a left edge goes on the first cell via `[&>td:first-child]:…`.
   */
  rowClassName?: (row: TData) => string | undefined;
  /**
   * Drop the Columns toggle from the default card. The toolbar row then renders
   * only when the caller supplies a `filterBar` or `action`, so a table with
   * neither sits directly under whatever precedes it.
   */
  hideColumnToggle?: boolean;
  /**
   * Give the card the system's one elevation step (shadow-md plus a hairline
   * ring) instead of sitting flat on the page. For a table that is the whole
   * point of its screen rather than one panel among several.
   */
  elevated?: boolean;
  /**
   * Floor for the table's own width, in pixels, below which its container
   * scrolls instead of squeezing the columns.
   *
   * Needed whenever some columns declare a `width` and one does not: the table
   * switches to fixed layout, and once the declared widths no longer fit, the
   * unsized column is the one that collapses — to nothing, taking its content
   * with it. Set this above the sum of the declared widths so that column
   * always keeps a share.
   */
  minTableWidth?: number;
  /**
   * Shrink the card to the sum of its declared column widths instead of filling
   * the row. For a table whose columns are all sized, where stretching to the
   * page would only add empty space between them.
   */
  fitContent?: boolean;
  /** Extra text after the footer count, e.g. a second tally. */
  footerExtra?: ReactNode;
  /** Classes for the header row's cells, e.g. a shorter header. */
  headerRowClassName?: string;
}

export default function DataTable<TData, TValue>({
  columns,
  data,
  filterBar,
  action,
  isLoading = false,
  emptyState,
  meta,
  itemNoun = { singular: "result" },
  initialColumnVisibility,
  columnFilters: controlledColumnFilters,
  onColumnFiltersChange,
  sorting: controlledSorting,
  onSortingChange,
  serverPagination,
  variant = "default",
  initialPageSize,
  paginateAfter,
  alwaysShowFooter = false,
  initialSorting,
  onRowClick,
  rowClassName,
  hideColumnToggle = false,
  elevated = false,
  minTableWidth,
  fitContent = false,
  footerExtra,
  headerRowClassName,
}: DataTableProps<TData, TValue>) {
  const embedded = variant === "embedded";
  // Opt-in: tables whose columns declare no width keep the browser's
  // content-driven sizing, so existing callers are untouched.
  const hasFixedColumnWidths = columns.some(
    (column) => columnMeta(column)?.width != null,
  );
  // A fit-content table already knows how wide it wants to be, so it scrolls
  // below that rather than squeezing its columns on a narrow screen.
  const fitWidth = fitContent
    ? columns.reduce((sum, column) => sum + (columnMeta(column)?.width ?? 0), 0)
    : 0;
  const [internalSorting, setInternalSorting] = useState<SortingState>(
    initialSorting ?? [],
  );
  const [internalColumnFilters, setInternalColumnFilters] =
    useState<ColumnFiltersState>([]);
  // Controlled when the caller supplies both props; otherwise self-managed.
  const columnFilters = controlledColumnFilters ?? internalColumnFilters;
  const setColumnFilters: OnChangeFn<ColumnFiltersState> =
    onColumnFiltersChange ?? setInternalColumnFilters;
  const sorting = controlledSorting ?? internalSorting;
  const setSorting: OnChangeFn<SortingState> =
    onSortingChange ?? setInternalSorting;
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    initialColumnVisibility ?? {},
  );
  const [rowSelection, setRowSelection] = useState({});
  const clientPageSize = resolveClientPageSize(
    data.length,
    paginateAfter,
    initialPageSize,
  );
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: clientPageSize,
  });

  useEffect(() => {
    if (serverPagination) {
      return;
    }
    setPagination((prev) =>
      prev.pageSize === clientPageSize
        ? prev
        : { pageIndex: 0, pageSize: clientPageSize },
    );
  }, [clientPageSize, serverPagination]);

  const table = useReactTable({
    data,
    columns,
    meta,
    manualPagination: !!serverPagination,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: serverPagination ? undefined : setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      ...(serverPagination ? {} : { pagination }),
    },
  });

  const pageIndex = serverPagination
    ? serverPagination.pageIndex
    : table.getState().pagination.pageIndex;
  const pageSize = serverPagination
    ? serverPagination.pageSize
    : table.getState().pagination.pageSize;
  const totalRows = serverPagination
    ? serverPagination.totalRows
    : table.getFilteredRowModel().rows.length;
  const pageCount = serverPagination
    ? Math.max(1, Math.ceil(totalRows / pageSize))
    : table.getPageCount() || 1;
  const firstRow = totalRows === 0 ? 0 : pageIndex * pageSize + 1;
  const lastRow = Math.min((pageIndex + 1) * pageSize, totalRows);
  const canPreviousPage = serverPagination
    ? pageIndex > 0
    : table.getCanPreviousPage();
  const canNextPage = serverPagination
    ? pageIndex < pageCount - 1
    : table.getCanNextPage();
  const goToPreviousPage = () =>
    serverPagination
      ? serverPagination.onPageChange(pageIndex - 1)
      : table.previousPage();
  const goToNextPage = () =>
    serverPagination
      ? serverPagination.onPageChange(pageIndex + 1)
      : table.nextPage();
  const noun =
    totalRows !== 1
      ? (itemNoun.plural ?? `${itemNoun.singular}s`)
      : itemNoun.singular;
  const hasHiddenColumns = table
    .getAllColumns()
    .some((col) => col.getCanHide() && !col.getIsVisible());

  return (
    <div
      className={cn(
        fitContent ? "w-full max-w-full lg:w-fit" : "w-full",
        !embedded && "rounded-xl border border-border overflow-hidden bg-card",
        !embedded && elevated && "shadow-md ring-1 ring-foreground/5",
      )}
    >
      {((!embedded && !hideColumnToggle) || filterBar || action) && (
        <div
          className={cn(
            "flex items-center justify-between gap-2",
            embedded ? "pb-3" : "px-4 py-3 border-b border-border",
          )}
        >
          <div className="flex items-center gap-2">
            {filterBar?.(table)}
            {/* Column toggle is card chrome — omitted in the embedded (blended)
              variant, but a caller's filterBar/action still render above. */}
            {!embedded && !hideColumnToggle && (
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5 text-xs font-normal rounded-md bg-surface dark:bg-surface text-text-muted hover:text-text-primary"
                    />
                  }
                >
                  <Settings2 data-icon="inline-start" />
                  Columns
                  {hasHiddenColumns && (
                    <span className="size-1.5 rounded-full bg-primary" />
                  )}
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="w-44 text-xs rounded-lg"
                >
                  <DropdownMenuGroup>
                    <DropdownMenuLabel className="text-xs poppins">
                      Toggle columns
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {table
                      .getAllColumns()
                      .filter((col) => col.getCanHide())
                      .map((col) => (
                        <DropdownMenuCheckboxItem
                          key={col.id}
                          className="text-xs"
                          checked={col.getIsVisible()}
                          onCheckedChange={(value) =>
                            col.toggleVisibility(!!value)
                          }
                        >
                          {typeof col.columnDef.header === "string"
                            ? col.columnDef.header
                            : humanizeColumnId(col.id)}
                        </DropdownMenuCheckboxItem>
                      ))}
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          {action && <div>{action}</div>}
        </div>
      )}

      <div className="overflow-auto">
        {/* `table-fixed` only once a column asks for a width. Without it the
            browser sizes columns from their content, so one row whose cell is
            wider than the rest — a badge under a number, say — widens that
            column for itself and the column loses its common edge down the
            page. Fixed layout takes the declared widths instead, and no row's
            content can argue with them. */}
        <Table
          className={cn(
            hasFixedColumnWidths && "table-fixed",
            fitContent && "lg:w-auto",
          )}
          style={
            minTableWidth || fitWidth
              ? { minWidth: minTableWidth ?? fitWidth }
              : undefined
          }
        >
          <TableHeader
            className={cn(
              "[&_tr]:border-b",
              !embedded && "sticky top-0 z-10 bg-muted",
            )}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                key={headerGroup.id}
                className="hover:bg-transparent border-b border-border"
              >
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={cn(
                      "px-4 py-2 text-xs poppins font-medium uppercase tracking-[0.06em] text-text-muted whitespace-nowrap transition-colors",
                      header.column.getIsSorted() &&
                        "bg-primary/[0.04] text-primary",
                      columnMeta(header.column.columnDef)?.className,
                      columnMeta(header.column.columnDef)?.headerClassName,
                      headerRowClassName,
                    )}
                    style={widthStyle(header.column.columnDef)}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: SKELETON_ROWS }).map((_, rowIdx) => (
                <TableRow key={rowIdx} className="border-b border-border">
                  {table.getVisibleLeafColumns().map((col, colIdx) => (
                    <TableCell
                      key={col.id}
                      className={cn(
                        "px-4 py-5 text-xs mulish",
                        columnMeta(col.columnDef)?.className,
                      )}
                      style={widthStyle(col.columnDef)}
                    >
                      <Skeleton
                        className={cn(
                          "h-3.5 rounded-md",
                          SKELETON_CELL_WIDTHS[
                            colIdx % SKELETON_CELL_WIDTHS.length
                          ],
                        )}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row, rowIdx) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() ? "selected" : undefined}
                  className={cn(
                    "group/row border-b border-border transition-colors animate-in fade-in-0 slide-in-from-bottom-1 duration-200 motion-reduce:animate-none",
                    onRowClick
                      ? "cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring"
                      : "hover:bg-muted/50",
                    rowClassName?.(row.original),
                  )}
                  style={{
                    animationDelay: `${rowIdx * 18}ms`,
                    animationFillMode: "both",
                  }}
                  // Deliberately no role override: a <tr> must stay a row, or
                  // the table stops being a table for assistive tech. The row
                  // is reachable and Enter/Space activate it; naming the
                  // action properly needs a real control inside a cell.
                  tabIndex={onRowClick ? 0 : undefined}
                  onClick={
                    onRowClick
                      ? (event) => {
                          if (shouldIgnoreRowClick(event.target)) {
                            return;
                          }
                          onRowClick(row.original);
                        }
                      : undefined
                  }
                  onKeyDown={
                    onRowClick
                      ? (event) => {
                          if (event.key !== "Enter" && event.key !== " ") {
                            return;
                          }
                          if (shouldIgnoreRowClick(event.target)) {
                            return;
                          }
                          event.preventDefault();
                          onRowClick(row.original);
                        }
                      : undefined
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        "px-4 py-5 mulish transition-colors",
                        onRowClick &&
                          "group-hover/row:bg-muted group-active/row:bg-muted/80",
                        cell.column.getIsSorted() &&
                          "bg-primary/[0.03] group-hover/row:bg-muted group-active/row:bg-muted/80",
                        columnMeta(cell.column.columnDef)?.className,
                      )}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : columnFilters.length > 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="p-0">
                  <div className="flex h-24 flex-col items-center justify-center gap-2">
                    <p className="text-xs mulish text-text-muted">
                      No {itemNoun.plural ?? `${itemNoun.singular}s`} match your
                      filters.
                    </p>
                    <Button
                      variant="link"
                      size="sm"
                      className="text-xs mulish h-auto p-0"
                      onClick={() => setColumnFilters([])}
                    >
                      Clear filters
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="p-0">
                  {emptyState ?? (
                    <div className="flex h-24 items-center justify-center text-xs mulish text-text-muted">
                      No results.
                    </div>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {(!embedded || pageCount > 1 || alwaysShowFooter) && (
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-border">
          <p className="text-xs mulish text-text-muted">
            {totalRows === 0 ? (
              "No results"
            ) : pageCount > 1 ? (
              <>
                <span className="text-foreground font-medium tabular-nums">
                  {firstRow}–{lastRow}
                </span>{" "}
                of {totalRows} {noun}
              </>
            ) : (
              <>
                <span className="text-foreground font-medium tabular-nums">
                  {totalRows}
                </span>{" "}
                {noun}
              </>
            )}
            {footerExtra}
          </p>
          {pageCount > 1 && (
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="sm"
                className="size-7 p-0 rounded-md"
                aria-label="Previous page"
                onClick={goToPreviousPage}
                disabled={!canPreviousPage}
              >
                <ChevronLeft />
              </Button>
              <span className="text-xs mulish px-1">
                <span className="text-foreground font-semibold tabular-nums">
                  {pageIndex + 1}
                </span>
                <span className="text-text-muted"> / {pageCount}</span>
              </span>
              <Button
                variant="outline"
                size="sm"
                className="size-7 p-0 rounded-md"
                aria-label="Next page"
                onClick={goToNextPage}
                disabled={!canNextPage}
              >
                <ChevronRight />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
